import type { ReactNode } from 'react';

import type { InboxItem } from '../fixtures/domain.js';
import { AssistantMark } from '../components/AssistantMark.js';
import { Avatar } from '../components/Avatar.js';
import { Checkbox } from '../components/Checkbox.js';
import { Icon } from '../components/Icon.js';
import { Marked } from '../components/Marked.js';
import { PlainMessageText } from '../components/MessageMarkdown.js';
import { isEnded } from '../fixtures/rules.js';
import { formatInboxTime } from '../util/format.js';
import { DeletionPill } from './DeletionPill.js';
import styles from './ChannelPreview.module.css';

interface ChannelPreviewProps {
  item: InboxItem;
  active: boolean;
  onClick: () => void;
  /**
   * Selection is opt-in, and only the inbox opts in. The widget's list is the
   * reply loop, so it does not grow a checkbox just because it shares this row.
   */
  selectable?: boolean;
  selected?: boolean;
  /** True once anything in the list is selected (drives `data-selecting`). */
  selecting?: boolean;
  onToggleSelect?: () => void;
  /**
   * This row is showing one particular message rather than the conversation's
   * latest — the Messages group's rows, each a search hit. It dates the row by
   * that message and drops the posting chip and the assistant's mark, which
   * belong to the conversation rather than to the line on screen (and would
   * repeat on every hit from one chat). Everything else is untouched, because
   * it IS that conversation, seen through a different question.
   */
  message?: { preview: ReactNode; at: string };
  /**
   * What the employer is searching for, so the row can pick it out of its own
   * name and posting. A name match that showed no mark is the whole bug: a
   * filtered list whose rows carry nothing the employer typed reads as a guess.
   */
  term?: string;
}

export function ChannelPreview({
  item,
  active,
  onClick,
  selectable,
  selected = false,
  selecting = false,
  onToggleSelect,
  message,
  term = '',
}: ChannelPreviewProps) {
  const { channel, counterpart } = item;
  const hasUnread = channel.unreadCount > 0;
  // Suppressed on a search hit; see the `message` prop.
  const jobTitle = message ? undefined : channel.jobMeta?.jobTitle;
  /**
   * Derived here rather than passed in, so it cannot drift from the rest of the
   * app. Only the widget's list holds ended chats (AI-2647) — /chat's inbox
   * excludes them by query and its Ended rail draws its own row — but a row that
   * decided this from a prop would be a row two callers could disagree about.
   */
  const ended = isEnded(channel);
  const row = (
    <button
      className={active ? styles.rowActive : styles.row}
      // A plain attribute rather than a fifth class: `ended` crosses `active`,
      // and the muting is CSS's business alone.
      data-ended={ended || undefined}
      onClick={onClick}
    >
      <Avatar name={counterpart.name} src={counterpart.avatarUrl} locked={counterpart.locked} />
      <span className={styles.body}>
        <span className={styles.line}>
          <span className={styles.nameWrap}>
            {counterpart.online !== undefined && (
              <span className={counterpart.online ? styles.dotOnline : styles.dotOffline} />
            )}
            {/* Masked in a replay (AI-3016). The inbox is the screen an
                employer spends most of their day on, so this is the most
                exposed candidate data in the product. */}
            <span data-ph-mask className={styles.name}>
              <Marked text={counterpart.name} term={term} />
            </span>
          </span>
          {/* When a chat was last active stops being the useful fact once it is
              ending; how long it has left takes the slot, the same swap /chat's
              Ended rail makes. */}
          {ended ? (
            <DeletionPill channel={channel} />
          ) : (
            <span className={styles.time}>
              {formatInboxTime(message?.at ?? channel.lastActivityAt)}
            </span>
          )}
        </span>
        {jobTitle && (
          <span className={styles.jobTag}>
            <Icon name="briefcase" size={11} />
            <span className={styles.jobTagText}>
              {/* Marked as well as the name: a chat is named by its person AND
                  its posting, so either half can be why it is here. */}
              <Marked text={jobTitle} term={term} />
            </span>
          </span>
        )}
        <span className={styles.previewLine}>
          {/* The last message, which is transcript by another name. */}
          <span data-ph-mask className={hasUnread ? styles.previewUnread : styles.preview}>
            {message ? (
              message.preview
            ) : channel.lastMessageHasAttachment ? (
              // Matches @ajt/stream-chat's preview (AI-1738): the clip shows on
              // every attachment row, and the caption replaces the label only
              // when there is one — a caption says more than "Attachment" does.
              <span className={styles.previewAttachment}>
                <Icon name={channel.lastMessageIsVoice ? 'mic' : 'paperclip'} size={13} />
                <span>
                  {channel.lastMessageText !== '' ? (
                    <PlainMessageText text={channel.lastMessageText} />
                  ) : channel.lastMessageIsVoice ? (
                    'Voice note'
                  ) : (
                    'Attachment'
                  )}
                </span>
              </span>
            ) : channel.lastMessageText === '' ? (
              // A channel created but never messaged in (AI-2664). Italic and
              // muted so it reads as the absence of a message rather than as
              // someone having actually sent the words.
              <span className={styles.previewEmpty}>No messages yet</span>
            ) : (
              <PlainMessageText text={channel.lastMessageText} />
            )}
          </span>
          {/* Unread speaks in weight: `.previewUnread` above goes bold, and
              that is the whole of the mark. A red dot stood here too, so the row
              said one thing twice — and the second telling was the expensive
              one, because it spent red on the commonest state in the column.
              Red is reserved for the assistant's verdict, which is rare by
              construction and worth crossing a room for; unread is neither.

              Bold says nothing out loud, so the state also exists as a word only
              a screen reader receives. Ended chats included: being read is
              something an ended chat can still be owed. */}
          {hasUnread && <span className={styles.srOnly}>Unread</span>}
          {/* The assistant's verdict: there is a decision to make on this
              conversation. The only red on the row now that unread speaks in
              weight, which is what lets it mean one thing rather than compete
              to be told apart.

              Unlike unread it survives being looked at — only a fresh reading
              that stops recommending a decision retires it, which usually means
              the employer took the step. The mark they click through is the one
              the card's header wears, which is what makes following it worth
              anything. */}
          {item.channel.assistantFlag === true && !message && <AssistantMark />}
        </span>
      </span>
    </button>
  );

  if (!selectable) return row;

  return (
    // The box lives beside the row, not inside it: a control nested in the row's
    // own button is invalid markup, and one sharing the row's click target would
    // make "select" and "open" the same gesture.
    <div
      className={active ? `${styles.selectable} ${styles.isActive}` : styles.selectable}
      data-selecting={selecting || undefined}
    >
      {/* A label over the face, so the whole avatar toggles the box beside it. */}
      <label className={styles.zone}>
        <Checkbox
          checked={selected}
          label={`Select the chat with ${counterpart.name}`}
          onChange={() => onToggleSelect?.()}
        />
      </label>
      <span className={styles.spacer} aria-hidden="true" />
      {row}
    </div>
  );
}
